import React, { useEffect, useState } from "react";
import api from "../../../api/client";

const MODES = [
  { value: "air", label: "By Air" },
  { value: "sea", label: "By Sea" },
];
const MODE_LABEL = { air: "By Air", sea: "By Sea" };

const inputStyle = {
  width: "100%", padding: "7px 10px", border: "1px solid #d6dfed",
  borderRadius: 7, fontSize: 13, boxSizing: "border-box",
};
const labelStyle = {
  display: "block", fontSize: 11, fontWeight: 700,
  color: "#4f6280", marginBottom: 3,
};

function formatDelivered(iso) {
  if (!iso) return null;
  return new Date(iso).toLocaleDateString("en-IN", {
    day: "numeric", month: "short", year: "numeric",
  });
}

const emptyForm = { trackingNumber: "", invoiceNumber: "", modeOfShipment: "air", quantity: "" };

// Stable identity — a fresh [] default would retrigger the prop-sync effect every render
const NO_SHIPMENTS = [];

function ShipmentForm({ initial, maxQuantity, onSave, onCancel, saving }) {
  const [form, setForm] = useState(initial ?? emptyForm);
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  return (
    <form
      onSubmit={(e) => { e.preventDefault(); onSave(form); }}
      style={{ background: "#f8fafc", border: "1px solid #e4ebf5", borderRadius: 8, padding: 12, marginTop: 8 }}
    >
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
        <div>
          <label style={labelStyle}>Tracking Number *</label>
          <input style={inputStyle} value={form.trackingNumber} onChange={set("trackingNumber")} required autoFocus />
        </div>
        <div>
          <label style={labelStyle}>Invoice Number *</label>
          <input style={inputStyle} value={form.invoiceNumber} onChange={set("invoiceNumber")} required />
        </div>
        <div>
          <label style={labelStyle}>Mode of Shipment *</label>
          <select style={inputStyle} value={form.modeOfShipment} onChange={set("modeOfShipment")}>
            {MODES.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
          </select>
        </div>
        <div>
          <label style={labelStyle}>Quantity * <span style={{ fontWeight: 400, color: "#94a3b8" }}>(max {maxQuantity})</span></label>
          <input
            style={inputStyle} type="number" min="1" max={maxQuantity}
            value={form.quantity} onChange={set("quantity")} required
          />
        </div>
      </div>
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
        <button
          type="button" onClick={onCancel} disabled={saving}
          style={{ padding: "6px 14px", borderRadius: 7, border: "1px solid #d6dfed", background: "#fff", cursor: "pointer", fontSize: 12, fontWeight: 600, color: "#475569" }}
        >
          Cancel
        </button>
        <button
          type="submit" disabled={saving}
          style={{ padding: "6px 16px", borderRadius: 7, border: "none", background: "#0f2d5c", color: "#fff", cursor: saving ? "default" : "pointer", fontSize: 12, fontWeight: 700, opacity: saving ? 0.6 : 1 }}
        >
          {saving ? "Saving…" : "Save Shipment"}
        </button>
      </div>
    </form>
  );
}

// Shipment log for a `dispatch` stage. Marking a shipment delivered stamps its
// delivery date and reports its parts as stage progress, so the stage closes
// itself once every part has landed.
export default function DispatchPanel({
  stageProgressId,
  shipments: initialShipments = NO_SHIPMENTS,
  partsReceived = 0,
  onChanged,
}) {
  const [shipments, setShipments] = useState(initialShipments);
  const [editing, setEditing] = useState(null); // 'new' | shipment id
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => { setShipments(initialShipments); }, [initialShipments]);

  const allocated = shipments.reduce((sum, s) => sum + s.quantity, 0);
  const delivered = shipments.filter((s) => s.deliveredAt).reduce((sum, s) => sum + s.quantity, 0);
  const unallocated = partsReceived - allocated;

  const run = async (fn) => {
    setBusy(true);
    setError("");
    try {
      await fn();
      setEditing(null);
      onChanged?.();
    } catch (err) {
      setError(err.response?.data?.error || "Something went wrong. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const handleSave = (form) => run(async () => {
    const payload = { ...form, quantity: Number(form.quantity) };
    if (editing === "new") {
      const res = await api.post(`/manufacturing/stages/${stageProgressId}/shipments`, payload);
      setShipments((prev) => [...prev, res.data.data]);
    } else {
      const res = await api.patch(`/manufacturing/stages/shipments/${editing}`, payload);
      setShipments((prev) => prev.map((s) => (s.id === editing ? res.data.data : s)));
    }
  });

  const handleDeliver = (id) => run(async () => {
    const res = await api.post(`/manufacturing/stages/shipments/${id}/deliver`);
    setShipments((prev) => prev.map((s) => (s.id === id ? res.data.data : s)));
  });

  const handleDelete = (id) => run(async () => {
    await api.delete(`/manufacturing/stages/shipments/${id}`);
    setShipments((prev) => prev.filter((s) => s.id !== id));
  });

  const editingShipment = shipments.find((s) => s.id === editing);

  return (
    <div style={{ marginTop: 10, borderTop: "1px dashed #e4ebf5", paddingTop: 10 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: "#4f6280", textTransform: "uppercase", letterSpacing: "0.4px" }}>
          🚚 Shipments <span style={{ fontWeight: 400, color: "#94a3b8" }}>({shipments.length})</span>
        </div>
        <div style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 11, color: "#64748b" }}>
          <span>Delivered <strong style={{ color: "#15803d" }}>{delivered}</strong> / {partsReceived}</span>
          {unallocated > 0 && (
            <span style={{ background: "#fff7ed", color: "#c2410c", border: "1px solid #fed7aa", borderRadius: 999, padding: "1px 8px", fontWeight: 700 }}>
              {unallocated} unallocated
            </span>
          )}
        </div>
      </div>

      {shipments.length === 0 && editing !== "new" && (
        <div style={{ fontSize: 12, color: "#94a3b8", padding: "8px 0" }}>
          No shipments recorded yet.
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 8 }}>
        {shipments.map((s) => (
          <div
            key={s.id}
            style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              gap: 10, flexWrap: "wrap",
              background: s.deliveredAt ? "#f0fdf4" : "#fff",
              border: `1px solid ${s.deliveredAt ? "#bbf7d0" : "#e4ebf5"}`,
              borderRadius: 8, padding: "8px 10px",
            }}
          >
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#0f2d5c" }}>
                {s.trackingNumber}
                <span style={{ fontWeight: 400, color: "#64748b" }}> · {s.quantity} units</span>
              </div>
              <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>
                Invoice {s.invoiceNumber} · {MODE_LABEL[s.modeOfShipment] || s.modeOfShipment}
                {s.deliveredAt && ` · Delivered ${formatDelivered(s.deliveredAt)}`}
              </div>
            </div>
            <div style={{ display: "flex", gap: 5, flexShrink: 0 }}>
              {s.deliveredAt ? (
                <span style={{ background: "#dcfce7", color: "#15803d", border: "1px solid #86efac", borderRadius: 999, padding: "2px 10px", fontSize: 11, fontWeight: 700 }}>
                  ✓ Delivered
                </span>
              ) : (
                <>
                  <button
                    onClick={() => handleDeliver(s.id)} disabled={busy}
                    style={{ padding: "4px 10px", background: "#dcfce7", color: "#15803d", border: "1px solid #86efac", borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: busy ? "default" : "pointer" }}
                  >
                    Mark Delivered
                  </button>
                  <button
                    onClick={() => { setError(""); setEditing(s.id); }} disabled={busy}
                    style={{ padding: "4px 10px", background: "#fff", color: "#475569", border: "1px solid #d6dfed", borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: busy ? "default" : "pointer" }}
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(s.id)} disabled={busy}
                    style={{ padding: "4px 8px", background: "#fff", color: "#dc2626", border: "1px solid #fca5a5", borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: busy ? "default" : "pointer" }}
                  >
                    ✕
                  </button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>

      {error && <p style={{ margin: "8px 0 0", fontSize: 12, color: "#dc2626" }}>{error}</p>}

      {editing ? (
        <ShipmentForm
          key={editing}
          initial={editingShipment && {
            trackingNumber: editingShipment.trackingNumber,
            invoiceNumber: editingShipment.invoiceNumber,
            modeOfShipment: editingShipment.modeOfShipment,
            quantity: String(editingShipment.quantity),
          }}
          maxQuantity={editing === "new" ? unallocated : unallocated + (editingShipment?.quantity ?? 0)}
          onSave={handleSave}
          onCancel={() => { setEditing(null); setError(""); }}
          saving={busy}
        />
      ) : (
        unallocated > 0 && (
          <button
            onClick={() => { setError(""); setEditing("new"); }}
            style={{ marginTop: 8, padding: "6px 12px", background: "#eff6ff", color: "#1d4ed8", border: "1px solid #bfdbfe", borderRadius: 7, fontSize: 12, fontWeight: 700, cursor: "pointer" }}
          >
            + Add Shipment
          </button>
        )
      )}
    </div>
  );
}
